/// <amd-module name="ServiceHooks/Web/Admin/Components/ServiceHooksView/ServiceHooksView" />
import * as Resources from "ServiceHooks/Web/Admin/Resources";
import { sanitizeLinks } from "Utilities";
import { IObservableArrayEventArgs, ObservableArrayAction, useObservable, useObservableArray } from "VSS/Core/Observable";
import { IdentityRef } from "VSS/Platform/Contracts/WebApi";
import { ConditionalChildren } from "VSSUI/ConditionalChildren";
import { Header, TitleSize } from "VSSUI/Header";
import { IHeaderCommandBarItem } from "VSSUI/HeaderCommandBar";
import { ListSelection } from "VSSUI/List";
import { MessageCard, MessageCardSeverity } from "VSSUI/MessageCard";
import { Observer } from "VSSUI/Observer";
import { Spinner, SpinnerSize } from "VSSUI/Spinner";
import { ITableColumn, SortOrder, sortItems } from "VSSUI/Table";
import { FILTER_CHANGE_EVENT, Filter } from "VSSUI/Utilities/Filter";
import { ZeroData, ZeroDataActionType } from "VSSUI/ZeroData";
import * as React from "react";
import { ServiceHooksDataContext } from "../../Context/ServiceHooksDataContext";
import { Permissions } from "../../Contracts/Permissions";
import { Consumer, Event, Subscription, SubscriptionStatus } from "../../Contracts/ServiceHooks";
import {
    getConsumerActionForSubscriptionAsString,
    getConsumerForSubscriptionAsString,
    getEventTypeForSubscriptionAsString,
    getSubscriptionStatusAsString
} from "../../Utilities";
import { SubscriptionFilterBar } from "../SubscriptionFilterBar/SubscriptionFilterBar";
import { SubscriptionTable } from "../SubscriptionTable/SubscriptionTable";
import { SubscriptionTableFilterIds } from "../SubscriptionTable/SubscriptionTable.Props";
import { IServiceHooksViewProps, ISortingArgs } from "./ServiceHooksView.Props";

function enableDisableMany(
    subscriptions: Subscription[],
    status: SubscriptionStatus,
    updateSubscriptionStatus: (status: SubscriptionStatus, subscription: Subscription) => void
) {
    subscriptions.forEach(x => updateSubscriptionStatus(status, x));
}

function getSubscriptionsFromSelection(subscriptions: Subscription[], selection: ListSelection) {
    return ([] as Subscription[]).concat(...selection.value.map(range => subscriptions.slice(range.beginIndex, range.endIndex + 1)));
}

const webHooksId = "webHooks";
export const ServiceHooksView: React.FunctionComponent<IServiceHooksViewProps> = props => {
    const {
        errorMessage,
        notificationSummaries,
        onCreateEditSubscription,
        onDeleteSubscriptions,
        onRefresh,
        onSubscriptionActivated,
        onUpdateSubscriptionStatus,
        subscriptions,
        userPermissions,
        zeroDataImagePath
    } = props;
    const dataContext = React.useContext(ServiceHooksDataContext);

    const tableFilter = React.useRef(new Filter());
    const [filteredItems, setFilteredItemsUnsafe] = useObservableArray([...subscriptions.value]);
    const selection = React.useRef(new ListSelection({ multiSelect: true, selectOnFocus: false }));
    const [displaySpinner, setDisplaySpinner] = useObservable(false);

    const setFilteredItems = (items: Subscription[]) => {
        const keysToSelect = getSubscriptionsFromSelection(filteredItems.value, selection.current).map(x => x.id);

        setFilteredItemsUnsafe(items);

        selection.current.clear();
        keysToSelect.forEach(key => {
            const index = filteredItems.value.findIndex(x => x.id === key);
            index > -1 && selection.current.select(index, 1, true);
        });
    };

    const getSubscriptionTableHeaderCommandBarItems = ()  => {
        const items: IHeaderCommandBarItem[] = [];
        if (subscriptions.length > 0) {
            
            const selectedSubscriptions = getSubscriptionsFromSelection(filteredItems.value, selection.current);

            const allSubscriptionsAlreadyDisabled = !selectedSubscriptions.some(
                subscription => subscription.status !== SubscriptionStatus.DisabledByUser 
                    && subscription.status !== SubscriptionStatus.DisabledByInactiveIdentity 
                    && subscription.status !== SubscriptionStatus.DisabledBySystem);
        
            const allSubscriptionsAlreadyEnabled = !selectedSubscriptions.some(subscription => subscription.status !== SubscriptionStatus.Enabled)

            items.push({
                id: "disable",
                text: Resources.TableMenuDisable,
                important: true,
                iconProps: { iconName: "CirclePause" },
                onActivate: () =>
                    enableDisableMany(
                        selectedSubscriptions,
                        SubscriptionStatus.DisabledByUser,
                        onUpdateSubscriptionStatus
                    ),
                disabled: 
                    !dataContext.dataLoaded.value || 
                    selection.current.value.length === 0 || 
                    allSubscriptionsAlreadyDisabled
            });

            items.push({
                id: "enable",
                text: Resources.TableMenuEnable,
                important: true,
                iconProps: { iconName: "Play" },
                onActivate: () =>
                    enableDisableMany(
                        selectedSubscriptions,
                        SubscriptionStatus.Enabled,
                        onUpdateSubscriptionStatus
                    ),
                disabled: 
                    !dataContext.dataLoaded.value || 
                    selection.current.value.length === 0 || 
                    allSubscriptionsAlreadyEnabled
            });

            if (userPermissions[Permissions.EditSubscriptions]) {
                items.push({
                    id: "delete",
                    text: Resources.TableMenuDelete,
                    important: true,
                    iconProps: { iconName: "Delete" },
                    onActivate: () => onDeleteSubscriptions(getSubscriptionsFromSelection(filteredItems.value, selection.current)),
                    disabled: !dataContext.dataLoaded.value || selection.current.value.length === 0
                });
            }
            
            items.push({
                id: "refresh",
                text: Resources.ButtonRefreshText,
                important: true,
                iconProps: { iconName: "Refresh" },
                onActivate: () => {
                    tableFilter.current.reset();
                    selection.current.clear();
                    onRefresh();
                },
                disabled: !dataContext.dataLoaded.value
            });
        }

        return items;
    }

    const getHeaderCommandBarItems = () => {
        const items: IHeaderCommandBarItem[] = [];

        if (userPermissions[Permissions.EditSubscriptions] && subscriptions.length > 0) {
            items.push({
                id: "create",
                text: Resources.ButtonCreateText,
                isPrimary: true,
                iconProps: { iconName: "Add" },
                onActivate: () =>
                    onCreateEditSubscription({
                        consumerId: dataContext.consumers[Math.max(0, dataContext.consumers.findIndex(x => x.id === webHooksId))].id,
                        consumerInputs: {},
                        publisherInputs: {}
                    }),
                disabled: !dataContext.dataLoaded.value
            });
        }
        return items;

    };

    const [subscriptionTableHeaderCommandBarItems, setSubscriptionTableCommandBarItems] = useObservableArray(getSubscriptionTableHeaderCommandBarItems);

    const [headerCommandBarItems, setHeaderCommandBarItems] = useObservableArray(getHeaderCommandBarItems);

    const onSubscriptionsChanged = (newData: IObservableArrayEventArgs<Subscription>, operation: ObservableArrayAction) => {
        return operation === "removeAll" || (operation === "push" && newData.addedItems && newData.addedItems.length === subscriptions.length);
    };

    React.useEffect(() => {
        setTimeout(() => {
            setDisplaySpinner(true);
        }, 200);

        const handler = () => {
            setSubscriptionTableCommandBarItems(getSubscriptionTableHeaderCommandBarItems());
            setHeaderCommandBarItems(getHeaderCommandBarItems());
        };

        subscriptions.subscribe(handler);
        dataContext.dataLoaded.subscribe(handler);
        selection.current.subscribe(handler);
        return () => {
            subscriptions.unsubscribe(handler);
            dataContext.dataLoaded.unsubscribe(handler);
            selection.current.unsubscribe(handler);
        };
    }, []);

    React.useEffect(() => {
        tableFilter.current.subscribe(updateFilteredItems, FILTER_CHANGE_EVENT);
        tableFilter.current.subscribe(updateSortFromCache, FILTER_CHANGE_EVENT);
        subscriptions.subscribe(updateFilteredItems);
        subscriptions.subscribe(updateSortFromCache);

        return () => {
            tableFilter.current.unsubscribe(updateFilteredItems, FILTER_CHANGE_EVENT);
            tableFilter.current.unsubscribe(updateSortFromCache, FILTER_CHANGE_EVENT);
            subscriptions.unsubscribe(updateFilteredItems);
            subscriptions.unsubscribe(updateSortFromCache);
        };
    }, []);

    const tableDeleteWrapper = (subscription: Subscription) => {
        onDeleteSubscriptions([subscription]);
    };

    const updateFilteredItems = () => {
        const filter = tableFilter.current;
        const filterConsumer = filter.getFilterItemValue<Consumer[]>(SubscriptionTableFilterIds.CONSUMER);
        const filterEvent = filter.getFilterItemValue<Event[]>(SubscriptionTableFilterIds.EVENT);
        const filterOwner = filter.getFilterItemValue<IdentityRef[]>(SubscriptionTableFilterIds.OWNER);
        const filterState = filter.getFilterItemValue<SubscriptionStatus[]>(SubscriptionTableFilterIds.STATE);
        const keywordState = filter.getFilterItemValue<String>(SubscriptionTableFilterIds.TEXT);

        const isLegalItem = (item: Subscription) => {
            if (filterConsumer && filterConsumer.length > 0 && !filterConsumer.some(testItem => item.consumerId === testItem.id)) {
                return false;
            }
            if (filterEvent && filterEvent.length > 0 && !filterEvent.some(testItem => item.eventType === testItem.id)) {
                return false;
            }
            if (
                filterOwner &&
                filterOwner.length > 0 &&
                !filterOwner.some(testItem => (item.createdBy ? item.createdBy.id === testItem.id : false))
            ) {
                return false;
            }
            if (filterState && filterState.length > 0 && !filterState.some(testItem => item.status === testItem)) {
                return false;
            }

            if (keywordState) {
                const { consumersById, consumerActionsById, eventTypesById } = dataContext;

                // Constructing the test string
                const keywordSplit = keywordState.toLocaleLowerCase().split(" ");
                const testItemAsString = [
                    getConsumerForSubscriptionAsString(consumersById, item),
                    getEventTypeForSubscriptionAsString(eventTypesById, item),
                    item.eventDescription,
                    getConsumerActionForSubscriptionAsString(consumerActionsById, item),
                    item.actionDescription,
                    item.createdBy ? item.createdBy.displayName : Resources.Unknown,
                    getSubscriptionStatusAsString(item.status)
                ]
                    .join(" ")
                    .toLocaleLowerCase();

                if (!keywordSplit.every(term => testItemAsString.indexOf(term) !== -1)) {
                    return false;
                }
            }

            return true;
        };

        setFilteredItems(subscriptions.value.filter(isLegalItem));
    };

    const sortOrderCache = React.useRef<ISortingArgs<Subscription> | undefined>(undefined);
    const onSort = (
        columnIndex: number,
        proposedSortOrder: SortOrder,
        sortFunctions: (((itemOne: Subscription, itemTwo: Subscription) => number) | null)[],
        tableColumns: ITableColumn<Subscription>[]
    ) => {
        sortOrderCache.current = { columnIndex, proposedSortOrder, sortFunctions, tableColumns };
        // sortItems mutates the array, so we need to build a new one before setting the value
        setFilteredItems(sortItems(columnIndex, proposedSortOrder, sortFunctions, tableColumns, [...filteredItems.value]));
    };

    const updateSortFromCache = () => {
        if (sortOrderCache.current) {
            const { columnIndex, proposedSortOrder, sortFunctions, tableColumns } = sortOrderCache.current;
            onSort(columnIndex, proposedSortOrder, sortFunctions, tableColumns);
        }
    };

    return (
        <React.Fragment>
            <Header
                description={Resources.HeaderDescription}
                title={Resources.HeaderTitle}
                titleSize={TitleSize.Large}
                commandBarItems={headerCommandBarItems}
            />

            <div className="page-content page-content-top flex-column flex-grow rhythm-vertical-8">
                <Observer
                    dataLoaded={dataContext.dataLoaded}
                    errorMessage={errorMessage}
                    subscriptions={{ observableValue: subscriptions, filter: onSubscriptionsChanged }}
                >
                    {(observerProps: { dataLoaded: boolean; errorMessage: string | undefined }) => {
                        if (observerProps.errorMessage) {
                            return <MessageCard severity={MessageCardSeverity.Error}>{sanitizeLinks(observerProps.errorMessage)}</MessageCard>;
                        } else if (observerProps.dataLoaded) {
                            return subscriptions.value.length > 0 ? (
                                <React.Fragment>
                                    <SubscriptionFilterBar filter={tableFilter.current} subscriptions={subscriptions} />
                                    <SubscriptionTable
                                        canEditSubscriptions={userPermissions[Permissions.EditSubscriptions]}
                                        consumersById={dataContext.consumersById}
                                        consumerActionsById={dataContext.consumerActionsById}
                                        eventTypesById={dataContext.eventTypesById}
                                        notificationSummaries={notificationSummaries}
                                        onRowActivated={onSubscriptionActivated}
                                        onSort={onSort}
                                        onSubscriptionDeleted={tableDeleteWrapper}
                                        onSubscriptionEdit={onCreateEditSubscription}
                                        onUpdateSubscriptionStatus={onUpdateSubscriptionStatus}
                                        selection={selection.current}
                                        headerCommandBarItems={subscriptionTableHeaderCommandBarItems}
                                        subscriptions={filteredItems}
                                    />
                                </React.Fragment>
                            ) : (
                                <ZeroData
                                    actionText={Resources.ButtonCreateText}
                                    actionType={ZeroDataActionType.ctaButton}
                                    onActionClick={() =>
                                        onCreateEditSubscription({
                                            consumerId:
                                                dataContext.consumers[Math.max(0, dataContext.consumers.findIndex(x => x.id === webHooksId))].id,
                                            consumerInputs: {},
                                            publisherInputs: {}
                                        })
                                    }
                                    imageAltText={Resources.ZeroDataPrimaryText}
                                    imagePath={zeroDataImagePath}
                                    primaryText={Resources.ZeroDataPrimaryText}
                                    secondaryText={Resources.ZeroDataSecondaryText}
                                />
                            );
                        } else {
                            return (
                                <ConditionalChildren renderChildren={displaySpinner}>
                                    <div className="flex-row flex-grow flex-center justify-center">
                                        <Spinner label={Resources.Loading} size={SpinnerSize.large} />
                                    </div>
                                </ConditionalChildren>
                            );
                        }
                    }}
                </Observer>
            </div>
        </React.Fragment>
    );
};
